import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 页面容器
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 页面标题区块
const TitleSection = styled('section', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  '@media (max-width: 768px)': {
    fontSize: '2rem'
  }
});

// 副标题
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 选项卡容器
const TabContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: '1rem',
  marginBottom: '2rem',
  flexWrap: 'wrap'
});

// 选项卡按钮
const TabButton = styled('button', {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '8px',
  fontWeight: 'medium',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '$backgroundSecondary',
  color: '$textPrimary',
  '&:hover': {
    backgroundColor: '$backgroundHover'
  },
  variants: {
    active: {
      true: {
        backgroundColor: '$primary',
        color: 'white'
      }
    }
  }
});

// 服务卡片容器
const ServicesContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: '2rem'
});

// 服务卡片
const ServiceCard = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  background: '$backgroundSecondary',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)'
  },
  variants: {
    highlighted: {
      true: {
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
      }
    }
  }
});

// 热门标签
const PopularTag = styled('div', {
  position: 'absolute',
  top: '12px',
  right: '12px',
  backgroundColor: '$primary',
  color: 'white',
  padding: '0.25rem 1rem',
  borderRadius: '20px',
  fontSize: '0.75rem',
  fontWeight: 'bold'
});

// 服务标头
const ServiceHeader = styled('div', {
  padding: '1.5rem',
  backgroundColor: '$backgroundPrimary',
  borderBottom: '1px solid $border'
});

// 服务标题
const ServiceTitle = styled('h3', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem'
});

// 服务类别
const ServiceType = styled('span', {
  display: 'inline-block',
  fontSize: '0.875rem',
  color: '$textSecondary',
  marginBottom: '1rem'
});

// 服务价格
const ServicePrice = styled('div', {
  fontSize: '1.75rem',
  fontWeight: 'bold'
});

// 服务价格单位
const ServicePriceUnit = styled('span', {
  fontSize: '1rem',
  fontWeight: 'normal',
  color: '$textSecondary'
});

// 服务特性容器
const ServiceFeatures = styled('div', {
  padding: '1.5rem',
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

// 服务特性列表
const FeatureList = styled('ul', {
  listStyle: 'none',
  padding: 0,
  margin: '0 0 1.5rem 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  flex: 1
});

// 服务特性项
const FeatureItem = styled('li', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  fontSize: '0.875rem',
  lineHeight: 1.5
});

// 勾选图标
const CheckIcon = styled('span', {
  color: '$primary',
  fontWeight: 'bold'
});

// 申请按钮
const ApplyButton = styled('a', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '0.75rem',
  backgroundColor: '$primary',
  color: 'white',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 'medium',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '$primaryDark'
  }
});

// 比较表格容器
const ComparisonContainer = styled('div', {
  marginTop: '4rem'
});

// 比较表格标题
const ComparisonTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: '2rem',
  '@media (max-width: 768px)': {
    fontSize: '1.5rem'
  }
});

// 表格容器
const TableContainer = styled('div', {
  overflowX: 'auto',
  borderRadius: '8px',
  border: '1px solid $border'
});

// 表格
const Table = styled('table', {
  width: '100%',
  borderCollapse: 'collapse'
});

// 表头
const TableHead = styled('thead', {
  backgroundColor: '$backgroundHighlight'
});

// 表行
const TableRow = styled('tr', {
  '&:not(:last-child)': {
    borderBottom: '1px solid $border'
  },
  '&:nth-child(even)': {
    backgroundColor: '$backgroundPrimary'
  }
});

// 表头单元格
const TableHeaderCell = styled('th', {
  padding: '1rem',
  textAlign: 'left',
  fontWeight: 'bold',
  fontSize: '1rem'
});

// 表格单元格
const TableCell = styled('td', {
  padding: '1rem',
  fontSize: '0.875rem'
});

/**
 * 矿机托管服务页面组件
 * 展示矿机托管服务的各种套餐和选项
 */
const MiningHotelServices: React.FC = () => {
  const { t } = useTranslation();
  // 选择托管类型的状态，默认为自有矿机
  const [selectedType, setSelectedType] = useState<string>('own');
  
  // 处理选项卡点击
  const handleTabClick = (type: string) => {
    setSelectedType(type);
  };
  
  // 托管服务数据
  const services = {
    // 自有矿机托管服务
    own: [
      {
        id: 'basic',
        title: t('products.miningHotel.services.own.basic.title'),
        type: t('products.miningHotel.services.own.basic.type'),
        price: '$0.075',
        unit: '/kWh',
        features: [
          t('products.miningHotel.services.own.basic.feature1'),
          t('products.miningHotel.services.own.basic.feature2'),
          t('products.miningHotel.services.own.basic.feature3'),
          t('products.miningHotel.services.own.basic.feature4')
        ],
        popular: false
      },
      {
        id: 'standard',
        title: t('products.miningHotel.services.own.standard.title'),
        type: t('products.miningHotel.services.own.standard.type'),
        price: '$0.065',
        unit: '/kWh',
        features: [
          t('products.miningHotel.services.own.standard.feature1'),
          t('products.miningHotel.services.own.standard.feature2'),
          t('products.miningHotel.services.own.standard.feature3'),
          t('products.miningHotel.services.own.standard.feature4'),
          t('products.miningHotel.services.own.standard.feature5')
        ],
        popular: true
      },
      {
        id: 'advanced',
        title: t('products.miningHotel.services.own.advanced.title'),
        type: t('products.miningHotel.services.own.advanced.type'),
        price: '$0.055',
        unit: '/kWh',
        features: [
          t('products.miningHotel.services.own.advanced.feature1'),
          t('products.miningHotel.services.own.advanced.feature2'),
          t('products.miningHotel.services.own.advanced.feature3'),
          t('products.miningHotel.services.own.advanced.feature4'),
          t('products.miningHotel.services.own.advanced.feature5'),
          t('products.miningHotel.services.own.advanced.feature6')
        ],
        popular: false
      }
    ],
    // 分拆矿机托管服务
    shared: [
      {
        id: 'fractional-10',
        title: t('products.miningHotel.services.shared.fraction10.title'),
        type: t('products.miningHotel.services.shared.fraction10.type'),
        price: '$199',
        unit: '',
        features: [
          t('products.miningHotel.services.shared.fraction10.feature1'),
          t('products.miningHotel.services.shared.fraction10.feature2'),
          t('products.miningHotel.services.shared.fraction10.feature3'),
          t('products.miningHotel.services.shared.fraction10.feature4')
        ],
        popular: false
      },
      {
        id: 'fractional-5',
        title: t('products.miningHotel.services.shared.fraction5.title'),
        type: t('products.miningHotel.services.shared.fraction5.type'),
        price: '$399',
        unit: '',
        features: [
          t('products.miningHotel.services.shared.fraction5.feature1'),
          t('products.miningHotel.services.shared.fraction5.feature2'),
          t('products.miningHotel.services.shared.fraction5.feature3'),
          t('products.miningHotel.services.shared.fraction5.feature4'),
          t('products.miningHotel.services.shared.fraction5.feature5')
        ],
        popular: true
      },
      {
        id: 'fractional-2',
        title: t('products.miningHotel.services.shared.fraction2.title'),
        type: t('products.miningHotel.services.shared.fraction2.type'),
        price: '$999',
        unit: '',
        features: [
          t('products.miningHotel.services.shared.fraction2.feature1'),
          t('products.miningHotel.services.shared.fraction2.feature2'),
          t('products.miningHotel.services.shared.fraction2.feature3'),
          t('products.miningHotel.services.shared.fraction2.feature4'),
          t('products.miningHotel.services.shared.fraction2.feature5'),
          t('products.miningHotel.services.shared.fraction2.feature6')
        ],
        popular: false
      }
    ]
  };
  
  // 当前显示的服务
  const currentServices = selectedType === 'own' ? services.own : services.shared;
  
  return (
    <PageContainer>
      <TitleSection>
        <Title>{t('products.miningHotel.services.title')}</Title>
        <Subtitle>{t('products.miningHotel.services.subtitle')}</Subtitle>
      </TitleSection>
      
      <TabContainer>
        <TabButton
          active={selectedType === 'own'}
          onClick={() => handleTabClick('own')}
        >
          {t('products.miningHotel.services.ownMinerTab')}
        </TabButton>
        <TabButton
          active={selectedType === 'shared'}
          onClick={() => handleTabClick('shared')}
        >
          {t('products.miningHotel.services.sharedMinerTab')}
        </TabButton>
      </TabContainer>
      
      <ServicesContainer>
        {currentServices.map(service => (
          <ServiceCard key={service.id} highlighted={service.popular}>
            {service.popular && (
              <PopularTag>{t('products.miningHotel.services.popular')}</PopularTag>
            )}
            <ServiceHeader>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceType>{service.type}</ServiceType>
              <ServicePrice>
                {service.price}
                <ServicePriceUnit>{service.unit}</ServicePriceUnit>
              </ServicePrice>
            </ServiceHeader>
            <ServiceFeatures>
              <FeatureList>
                {service.features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <CheckIcon>✓</CheckIcon>
                    {feature}
                  </FeatureItem>
                ))}
              </FeatureList>
              <ApplyButton href="https://h5-app.BITENGINE.com" target="_blank">
                {t('products.miningHotel.services.applyNow')}
              </ApplyButton>
            </ServiceFeatures>
          </ServiceCard>
        ))}
      </ServicesContainer>
      
      <ComparisonContainer>
        <ComparisonTitle>{t('products.miningHotel.services.comparisonTitle')}</ComparisonTitle>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('products.miningHotel.services.comparison.feature')}</TableHeaderCell>
                <TableHeaderCell>{t('products.miningHotel.services.own.basic.title')}</TableHeaderCell>
                <TableHeaderCell>{t('products.miningHotel.services.own.standard.title')}</TableHeaderCell>
                <TableHeaderCell>{t('products.miningHotel.services.own.advanced.title')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.energyCost')}</TableCell>
                <TableCell>$0.075/kWh</TableCell>
                <TableCell>$0.065/kWh</TableCell>
                <TableCell>$0.055/kWh</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.setupFee')}</TableCell>
                <TableCell>$50/矿机</TableCell>
                <TableCell>$30/矿机</TableCell>
                <TableCell>{t('products.miningHotel.services.comparison.free')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.maintenance')}</TableCell>
                <TableCell>{t('products.miningHotel.services.comparison.basic')}</TableCell>
                <TableCell>{t('products.miningHotel.services.comparison.priority')}</TableCell>
                <TableCell>{t('products.miningHotel.services.comparison.premium')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.monitoring')}</TableCell>
                <TableCell>✓</TableCell>
                <TableCell>✓</TableCell>
                <TableCell>✓</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.firmwareUpdates')}</TableCell>
                <TableCell>✓</TableCell>
                <TableCell>✓</TableCell>
                <TableCell>✓</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.customFirmware')}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>✓</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.insurance')}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>✓</TableCell>
                <TableCell>✓</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.minContract')}</TableCell>
                <TableCell>3 {t('products.miningHotel.services.comparison.months')}</TableCell>
                <TableCell>6 {t('products.miningHotel.services.comparison.months')}</TableCell>
                <TableCell>12 {t('products.miningHotel.services.comparison.months')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('products.miningHotel.services.comparison.support')}</TableCell>
                <TableCell>{t('products.miningHotel.services.comparison.email')}</TableCell>
                <TableCell>{t('products.miningHotel.services.comparison.emailChat')}</TableCell>
                <TableCell>{t('products.miningHotel.services.comparison.dedicated')}</TableCell>
              </TableRow>
            </tbody>
          </Table>
        </TableContainer>
      </ComparisonContainer>
    </PageContainer>
  );
};

export default MiningHotelServices; 